export type AccountSubtype = 'personal' | 'business'

export enum FiTier {
  WELL_SUPPORTED = 'well_supported',
  LIMITED_SUPPORT = 'limited_support',
  NOT_SUPPORTED = 'not_supported',
}

export interface SelectedInstitution {
  institutionId: string
  name: string
  tier: FiTier
  accountSubtype: AccountSubtype
}

export interface PlaidInstitutionResult {
  institutionId: string
  name: string
}

const VALID_TIERS = new Set<string>(Object.values(FiTier))
const VALID_SUBTYPES = new Set<string>(['personal', 'business'])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isSelectedInstitution(value: unknown): value is SelectedInstitution {
  if (!isRecord(value)) return false
  const { institutionId, name, tier, accountSubtype } = value
  return (
    typeof institutionId === 'string' &&
    typeof name === 'string' &&
    typeof tier === 'string' &&
    VALID_TIERS.has(tier) &&
    typeof accountSubtype === 'string' &&
    VALID_SUBTYPES.has(accountSubtype)
  )
}

/**
 * Safely parse a JSON string into a validated SelectedInstitution array.
 * Returns `null` for any invalid / missing data.
 */
export function parseInstitutionSelections(
  raw: string | null
): SelectedInstitution[] | null {
  if (!raw) return null
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed) || !parsed.every(isSelectedInstitution)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}
